import { useCallback } from 'react'
import { type NavigateOptions, type To, useNavigate } from 'router-v6'
import { isEmbedMode, toEmbedRoutePath } from 'v2/shared/utils/embed-session'

// Embed-safe navigate: prefixes absolute destinations with /embed in embed mode (identity
// elsewhere), so a bare path cannot reach the console login. Relative ones resolve under /embed.
export const toEmbedTo = (to: To): To => {
  if (typeof to === 'string') {
    return to.startsWith('/') ? toEmbedRoutePath(to) : to
  }
  return to.pathname?.startsWith('/') ? { ...to, pathname: toEmbedRoutePath(to.pathname) } : to
}

// The router's index of the current entry (0 = this document's first).
const routerHistoryIndex = (): number => (window.history.state as { idx?: number } | null)?.idx ?? 0

export const useAppNavigate = () => {
  const navigate = useNavigate()
  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        // The iframe shares session history with the host page: going back past its first entry
        // would navigate the partner's page, so embed lands on its agents list instead.
        if (isEmbedMode() && routerHistoryIndex() + to < 0) {
          navigate(toEmbedTo('/agents'), { replace: true })
          return
        }
        navigate(to)
        return
      }
      navigate(toEmbedTo(to), options)
    },
    [navigate],
  )
}
